import moment from "moment";

import { Diagnosis } from "@/Common/Api/MedfarDictionaries/Dto/Responses/Diagnosis";
import { FlatLookupView } from "@/Common/Api/MedfarDictionaries/Dto/Responses/FlatLookupView";
import { RemoveButton } from "@/Common/Components/Buttons/RemoveButton";
import { RadioButton } from "@/Common/Components/Inputs/RadioButton";
import { SelectLookupControl } from "@/Common/Components/Inputs/SelectLookupControl";
import { TextAreaResizable } from "@/Common/Components/Inputs/TextAreaResizable";
import { TextboxControlWithDatePicker } from "@/Common/Components/Inputs/TextboxControlWithDatePicker";
import { ArrayHelper } from "@/Common/Helpers/ArrayHelper";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { JSXHelper } from "@/Common/Helpers/JSXHelper";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { DateTimeWithPrecision } from "@/Common/Models/DateTimeWithPrecision";
import { LifeStageDatePrecisionEnum } from "@/Common/Models/LifeStageDatePrecisionEnum";
import { MedicalItemsTab } from "@/Common/Models/Patient/PatientEnums";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { FavoriteDiagnosisAutocompleteControl } from "@/Patient/Components/Diagnosis/FavoriteDiagnosisAutocompleteControl";
import { PatientMedicalItemsHelper } from "@/Patient/Helpers/PatientMedicalItemsHelper";
import { PatientMedicalSummaryHelper } from "@/Patient/Helpers/PatientMedicalSummaryHelper";
import { LifeStageModeEnum } from "@/Patient/Models/LifeStageModeEnum";
import { DiagnosisModel } from "@/Patient/Models/MedicalSummary/DiagnosisModel";
import { MedicalItemSubtypeEnum } from "@/Patient/Models/MedicalSummary/Enum/MedicalItemSubTypeEnum";
import { MedicalItemTypeEnum } from "@/Patient/Models/MedicalSummary/Enum/MedicalItemTypeEnum";
import { MedicalItem, MedicalItemModel } from "@/Patient/Models/MedicalSummary/MedicalItem";
import { MedicalItemProblemAndCondition, QualifiedDetail } from "@/Patient/Models/MedicalSummary/MedicalItemProblemAndCondition";
import { BaseMedicalItemsView, IItemDetailsRenderFunction, IMedicalItemsViewProps } from "./BaseMedicalItemsView";
import { BaseMedicalItemsViewState } from "./BaseMedicalItemsViewState";
import { LifeStageRow } from "./LifeStageRow";

import "./MedicalItemsMedicalProblem.css";

class MedicalItemsMedicalProblemViewState extends BaseMedicalItemsViewState {
    public Description: string;
    public QualifiedDetails: QualifiedDetail[];
    public AvailableDiagnosticStates: FlatLookupView[];
    public SelectedDiagnosticTypeLookupId: string;

    public SelectedMedicalProblemType: string;
    public SelectedMedicalProblemSubType: string;

    public VisibleInCPP: boolean;

    // when status = resolved
    public ResolutionDate: Date;
    public ResolutionDateText: string;
    public IsResolutionDateApproximative: boolean;
    public ResolutionDatePrecision: LifeStageDatePrecisionEnum;
    public Procedure: string;
    public ProcedureDatePrecision: LifeStageDatePrecisionEnum;
    public ProcedureDate: Date;
    public ProcedureDateText: string;
    public IsProcedureDateApproximative: boolean;

    public InactiveStatusLookupId: string;
    public ResolvedStatusLookupId: string;
    public ProblemDescriptionContext: string;

    public Id: string;
    public DisplayId: string;

    public MedicalProblemIds: string[];

    public static GetProblemTypeRadioButtonModels(): KeyValuePair<string, string>[] {
        return [
            new KeyValuePair(cacheStore.GetString("ProblemesActifsLabel"), MedicalItemSubtypeEnum.ProblemAndCondition_Ongoing.toString()),
            new KeyValuePair(cacheStore.GetString("PastMedicalHistoryLabel"), MedicalItemSubtypeEnum.ProblemAndCondition_Past.toString()),
            new KeyValuePair(cacheStore.GetString("SurgicalHistoryLabel"), MedicalItemSubtypeEnum.ProblemAndCondition_SurgicalPast.toString()),
            new KeyValuePair(cacheStore.GetString("PsychiatricHistoryLabel"), MedicalItemSubtypeEnum.ProblemAndCondition_Psychiatric.toString()),
            new KeyValuePair(cacheStore.GetString("AntecedentsObstetriquesLabel"), MedicalItemSubtypeEnum.ProblemAndCondition_Gynecology.toString())
        ];
    }
}

class MedicalItemsMedicalProblemView extends BaseMedicalItemsView<IMedicalItemsViewProps, MedicalItemsMedicalProblemViewState> {
    public componentWillMount(): void {
        var s = null;
        let cacheId = this.getItemCacheId(this.props);
        if (cacheId != null) {
            s = patientStore.getMedicalItemCachedState(this.props.PatientContactId, cacheId);
        }

        if (s == null) {
            s = new MedicalItemsMedicalProblemViewState();
            s.InactiveStatusLookupId = cacheStore.FindLookupByLookupCode("ESINACTIVE", "PROBLEMSTATUS").LookupID;
            s.ResolvedStatusLookupId = cacheStore.FindLookupByLookupCode("RESOLVED", "PROBLEMSTATUS").LookupID;
            s.ResolutionDatePrecision = LifeStageDatePrecisionEnum.Day;
            s.ProcedureDatePrecision = LifeStageDatePrecisionEnum.Day;
            s.SelectedMedicalProblemSubType = MedicalItemSubtypeEnum.ProblemAndCondition_Ongoing;
            s.AvailableDiagnosticStates = cacheStore.FindLookupsByTypeCode("DiagnosisStatus");
            s.AvailableMedicalProblemStatus = cacheStore.FindLookupsByTypeCode("PROBLEMSTATUS");
            s.Description = "";
            s.SelectedMedicalProblemStatusLookupId = "";
            s.QualifiedDetails = [];
            s.QualifiedDetails.push(this.DiagnosticNewClicked());

            s.LifeStageIsStartDateApproximative = false;
            s.LifeStageMode = LifeStageModeEnum.Date;
            s.LifeStageDatePrecision = LifeStageDatePrecisionEnum.Day;
            s.LifeStageAvailableAgeAtOnsetUnits = cacheStore.FindLookupsByTypeCode("DayYear");
            s.LifeStageAvailableLifestages = cacheStore.FindLookupsByTypeCode("LIFESTAGE");
        }

        this.setState(s);
    }

    public componentWillReceiveProps(nextProps: IMedicalItemsViewProps): void {
        // when the model changes, check if we have to render an item in edit mode, if so, we set the state so we can edit its values
        const model = nextProps.Model;

        if (model != this.props.Model) {
            const isEditing = this.isEditing(model);
            const medicalItems = model.get("MedicalItemsList")?.toArray() ?? [];

            if (isEditing && medicalItems && medicalItems.length > 0) {
                const latestNewItem = MedicalItemModel.sortByCreationDateDesc(medicalItems.filter(x => isGuidNullOrUndefinedOrEmpty(x.Id))).firstOrDefault();
                if (latestNewItem != null) {
                    // Use the new MedicalItem
                    this.SetEditedItemState(latestNewItem);
                }
                else {
                    // modify the last item if Uncommitted
                    const latestMedicalItem = MedicalItemModel.sortByCreationDateDesc(medicalItems).firstOrDefault();

                    if (latestMedicalItem != null && !latestMedicalItem.IsCommitted)
                        this.SetEditedItemState(latestMedicalItem);
                }
            }
        }
    }

    public componentDidMount(): void {
        $(window).trigger("resize");
    }

    public componentDidUpdate(): void {
        $(window).trigger("resize");
    }

    public componentWillUnmount(): void {
        patientStore.setMedicalItemCachedState(this.props.PatientContactId, this.state.Id, this.state);
    }

    public render(): JSX.Element {
        const listItemRenderFunction = (problem: MedicalItemProblemAndCondition): JSX.Element => {
            let lifeStageString = "";
            let diagnosisesRender: JSX.Element[] = [];
            let isTemplateInvalid = (problem != null) ? problem.EMRTemplateIsInvalid : false;

            lifeStageString = PatientMedicalSummaryHelper.GetLifeStageString(problem);

            if (problem.QualifiedDetails != null && problem.QualifiedDetails.length > 0) {
                problem.QualifiedDetails.forEach(qualifiedDetail => {
                    diagnosisesRender.push(
                        <p className="ellipsis bold" key={"lifeStage_" + qualifiedDetail.Id}>{settingsStore.configuration.get("ShowDiagnosisCode") === true && <span>{qualifiedDetail.Code} - </span>}{qualifiedDetail.Text}</p>
                    );
                });
            }

            return (
                <div className="med-note" key={"prob_" + problem.EMRTemplateId}>
                    <div className="flex-container medical-note">
                        <div className="flx-col a">
                            {diagnosisesRender}

                            {isTemplateInvalid && <p className="red-text">{cacheStore.GetString("InvalidMedicalItemLabel")}</p>}
                            <p className="ellipsis">{lifeStageString}</p>
                        </div>
                    </div>
                </div>
            );
        };

        const itemDetailsRenderFunction: IItemDetailsRenderFunction = (medicalProblem: MedicalItemProblemAndCondition): JSX.Element => {
            const diagnosesRender: JSX.Element[] = [];
            let lifeStageString = "";
            let labelRendered = false;

            if (medicalProblem.QualifiedDetails != null && medicalProblem.QualifiedDetails.length > 0) {
                medicalProblem.QualifiedDetails.map(qualifiedDetail => {
                    let diagStatus = "";
                    if (!(qualifiedDetail.StatusLookupId == null) && qualifiedDetail.StatusLookupId.length > 0)
                        diagStatus = "(" + cacheStore.GetLocalizedValueByLookupId(qualifiedDetail.StatusLookupId) + ")";

                    // get onset date string
                    lifeStageString = PatientMedicalSummaryHelper.GetLifeStageString(medicalProblem);

                    const diagnosesLabel = !labelRendered
                        ? cacheStore.GetString("DiagnosisLabel")
                        : "";

                    labelRendered = true;

                    let qualifiedDetailRow: JSX.Element = (
                        <tr key={"QualifiedDetail_" + qualifiedDetail.Id}>
                            <td className="c1">
                                <span>{diagnosesLabel}</span>
                            </td>
                            <td className="c2">
                                {settingsStore.configuration.get("ShowDiagnosisCode") === true &&
                                    <strong>{qualifiedDetail.Code} - </strong>
                                }
                                <strong>{MyleHelper.ReplaceEmptyString(qualifiedDetail.Text)} {diagStatus}</strong>
                            </td>
                        </tr>
                    );

                    diagnosesRender.push(qualifiedDetailRow);
                });
            }

            let problemStatusLookup = !(medicalProblem.ProblemStatusLookupId == null) ? cacheStore.GetLookupById(medicalProblem.ProblemStatusLookupId) : null;
            let isProblemInactive = !(problemStatusLookup == null) && problemStatusLookup.LookupCode.toUpperCase() == "ESINACTIVE";
            let isProblemResolved = !(problemStatusLookup == null) && problemStatusLookup.LookupCode.toUpperCase() == "RESOLVED";
            let resolutionDateDisplay = "";
            if (medicalProblem.DateResolved)
                resolutionDateDisplay = this.getDateApproxString(medicalProblem.DateResolved, medicalProblem.PrecisionDateResolved);
            let procedureDate = "";
            if (medicalProblem.ProcedureDate)
                procedureDate = this.getDateApproxString(medicalProblem.ProcedureDate, medicalProblem.PrecisionDateProcedure);

            return (
                <div className="medical-reaction-detail-box" key={"prob_" + medicalProblem.MedicalItemId}>
                    <table className="medical-reaction-details border">
                        <tbody>
                            {diagnosesRender}
                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("ProblemDescriptionDLabel")}</span>
                                </td>
                                <td className="c2">
                                    <span className="text-gray"> {JSXHelper.BreakStringIntoParagraphs(MyleHelper.ReplaceEmptyString(medicalProblem.ProblemDescription), true)} </span>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("StatusDLabel")}</span>
                                </td>
                                <td className="c2">
                                    <strong>{cacheStore.GetLocalizedValueByLookupId(medicalProblem.ProblemStatusLookupId)}</strong>
                                </td>
                            </tr>
                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("DateLabel")}</span>
                                </td>
                                <td className="c2">
                                    <strong>{lifeStageString}</strong>
                                </td>
                            </tr>
                            {isProblemInactive && (
                                <tr>
                                    <td className="c1">
                                        <span>{cacheStore.GetString("DateLabel")} {cacheStore.GetString("OfInactivityLabel")}</span>
                                    </td>
                                    <td className="c2">
                                        <strong>{resolutionDateDisplay}</strong>
                                    </td>
                                </tr>
                            )}
                            {isProblemResolved && (
                                <tr>
                                    <td className="c1">
                                        <span><strong>{cacheStore.GetString("ResolutionLabel").toUpperCase()}</strong></span>
                                    </td>
                                    <td className="c2" />
                                </tr>
                            )}
                            {isProblemResolved && (
                                <tr>
                                    <td className="c1">
                                        <span>{cacheStore.GetString("DateResolvedLabel")}</span>
                                    </td>
                                    <td className="c2">
                                        {resolutionDateDisplay}
                                    </td>
                                </tr>
                            )}
                            {isProblemResolved && (
                                <tr>
                                    <td className="c1">
                                        <span>{cacheStore.GetString("ProcedureLabel")}</span>
                                    </td>
                                    <td className="c2">
                                        <span className="text-gray">
                                            {JSXHelper.BreakStringIntoParagraphs(medicalProblem.Procedure, true)}
                                        </span>
                                    </td>
                                </tr>
                            )}
                            {isProblemResolved && (
                                <tr>
                                    <td className="c1">
                                        <span>{cacheStore.GetString("ProcedureDateLabel")}</span>
                                    </td>
                                    <td className="c2">
                                        {procedureDate}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            );
        };

        const itemDetailsEditRenderFunction: IItemDetailsRenderFunction = (medicalProblem: MedicalItemProblemAndCondition): JSX.Element => {
            const diagnosticsList: JSX.Element[] = [];
            let isDiagnosisLabelRendered = false;

            if (this.state.QualifiedDetails?.length > 0) {
                this.state.QualifiedDetails.forEach((qualifiedDetail, idx) => {
                    const showEdit = !!qualifiedDetail.isEditMode || (qualifiedDetail.Code == null || qualifiedDetail.Code.trim().length == 0);
                    const itemId = (qualifiedDetail.Id != null && qualifiedDetail.Id != GuidHelper.Empty()) ? qualifiedDetail.Id : qualifiedDetail.tmpIdForFrontEndOnly;
                    const orderNumber = qualifiedDetail.Position;
                    const diagnosesLabel = !isDiagnosisLabelRendered ? cacheStore.GetString("DiagnosisLabel") : "";
                    isDiagnosisLabelRendered = true;

                    let row: JSX.Element = (
                        <tr key={"diag_" + itemId + "_" + orderNumber}>
                            <td className="c1">
                                {idx == 0 && <span>{diagnosesLabel}</span>}
                            </td>

                            <td className="c2">
                                {!showEdit &&
                                    <span>{settingsStore.configuration.get("ShowDiagnosisCode") === true && <strong>{qualifiedDetail.Code} - </strong>} <strong> {qualifiedDetail.Text}  </strong> </span>
                                }
                                {showEdit && (
                                    <FavoriteDiagnosisAutocompleteControl
                                        id={"diagSearch" + idx}
                                        dataCy="medicalItems-diagnosis-search"
                                        valid={qualifiedDetail.Text?.trim().length > 0}
                                        debounce={400}
                                        onItemSelect={item => this.onDiagnosticItemSelect(item, qualifiedDetail)}
                                        onFavoriteItemSelect={item => this.onFavoriteDiagnosticItemSelect(item, qualifiedDetail)}
                                        onTextChange={text => this.onDiagnosticTextChange(text, qualifiedDetail)}
                                        newValue={qualifiedDetail.Text}
                                        siteId={this.props.Model.get("Encounter")?.SiteId || sessionStore.getCurrentEmployee().getActiveSite().getId()}
                                    />
                                )}
                            </td>
                            <td className="c3">
                                <SelectLookupControl
                                    dataCy="medicalItems-diagnosis-status"
                                    IncludeEmptyChoice={true}
                                    Value={qualifiedDetail.StatusLookupId}
                                    AvailableValues={this.state.AvailableDiagnosticStates.map(x => x.ToComboboxItem())}
                                    Item={qualifiedDetail}
                                    Callback={(statusId: string) => this.onDiagnosticStatusSelect(statusId, qualifiedDetail)}
                                />
                            </td>
                            <td className="c4">
                                <RemoveButton
                                    className="margin-sm-left"
                                    onRemove={() => this.onDiagnosticRemoveClicked(qualifiedDetail)}
                                />
                                {!showEdit && (
                                    <span
                                        className="padding-sm-left pointer bold"
                                        onClick={() => this.DiagnosticEditClicked(qualifiedDetail)}
                                    >
                                        <i className="fa fa-pencil" aria-hidden="true" />
                                    </span>
                                )}
                            </td>
                        </tr>
                    );

                    diagnosticsList.push(row);
                });
            }

            diagnosticsList.push(
                <tr key="diag_add">
                    <td className="c1" />
                    <td className="c2" />
                    <td className="c3" />
                    <td className="c4">
                        <span onClick={this.DiagnosticAddClicked.bind(this)} style={{ "paddingLeft": "10px", "color": "#16325C", "fontWeight": "bold", "cursor": "pointer" }}>{cacheStore.GetString("AddLabel")}</span>
                    </td>
                </tr>
            );

            let resDateApproxdisabled = "";
            if (this.state != null)
                resDateApproxdisabled = this.state.ResolutionDatePrecision == LifeStageDatePrecisionEnum.MonthYear ||
                    this.state.ResolutionDatePrecision == LifeStageDatePrecisionEnum.Year ? "disabled" : "";

            let procedureDateApproxDisabled = "";
            if (this.state != null)
                procedureDateApproxDisabled = this.state.ProcedureDatePrecision == LifeStageDatePrecisionEnum.MonthYear ||
                    this.state.ProcedureDatePrecision == LifeStageDatePrecisionEnum.Year ? "disabled" : "";

            return (
                <div className="medical-reaction-detail-box" key={"prob_edit" + medicalProblem.MedicalItemId}>
                    <table className="medical-problems-edit border medical-problems-responsive">
                        <tbody>
                            {diagnosticsList}

                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("ProblemDescriptionDLabel")}</span>
                                </td>
                                <td className="c1">
                                    <TextAreaResizable
                                        id={`diag_desc${(this.state.ProblemDescriptionContext ? this.state.ProblemDescriptionContext : this.state.Id)}`}
                                        dataCy="medicalItems-problem-description"
                                        minHeightPx={100}
                                        text={this.state.Description}
                                        isDisabled={false}
                                        onChange={value => this.MedicalProblemDescriptionChanged(value)}
                                        setCursorStartOnFocus={false}
                                        resetTextOnIdChange={true}
                                    />
                                </td>
                            </tr>

                            <tr>
                                <td className="c1">
                                    <span>{cacheStore.GetString("StatusLabel")}</span>
                                </td>
                                <td className="c1">
                                    <SelectLookupControl
                                        dataCy="medicalItems-problem-status"
                                        IncludeEmptyChoice={true}
                                        Value={this.state.SelectedMedicalProblemStatusLookupId}
                                        AvailableValues={this.state.AvailableMedicalProblemStatus.map(x => x.ToComboboxItem())}
                                        Callback={this.MedicalProblemStatusSelected.bind(this)}
                                    />
                                </td>
                            </tr>

                            <LifeStageRow
                                lifeStageMode={this.state.LifeStageMode}
                                lifeStageAvailableAgeAtOnsetUnits={this.state.LifeStageAvailableAgeAtOnsetUnits}
                                lifeStageAgeSelectedAgeAtOnsetUnit={this.state.LifeStageAgeSelectedgeAtOnsetUnit}
                                lifeStageSelectedLifeStageLookupId={this.state.LifeStageSelectedLifestageLookupId}
                                lifeStageAvailableLifeStages={this.state.LifeStageAvailableLifestages}
                                lifeStageAgeAtOnset={this.state.LifeStageAgeAtOnset}
                                lifeStageDatePrecision={this.state.LifeStageDatePrecision}
                                lifeStageDateTextboxValue={this.state.LifeStageDateTextboxValue}
                                lifeStageStartDate={this.state.LifeStageStartDate}
                                lifeStageIsStartDateApprox={this.state.LifeStageIsStartDateApproximative}
                                useThreeCols
                                onLifeStageChange={value => this.LifeStageChanged(value)}
                                onLifeStageDateChange={(date, precision) => this.LifeStageDateChanged(date, precision)}
                                onLifeStageModeClick={newMode => this.LifeStageModeChanged(newMode)}
                                onLifeStageDateApproxChange={() => this.LifeStageDateApproxChange()}
                                onLifeStageDateTodayClick={() => this.LifeStageDateTodayClicked()}
                                onLifeStageAgeChange={value => this.LifeStageAgeChange(value)}
                                onLifeStageAgeUnitChange={value => this.LifeStageAgeUnitChange(value)}
                            />

                            {(this.state.SelectedMedicalProblemStatusLookupId == this.state.InactiveStatusLookupId || this.state.SelectedMedicalProblemStatusLookupId == this.state.ResolvedStatusLookupId) && (
                                <tr>
                                    <td className="c1">
                                        {this.state.SelectedMedicalProblemStatusLookupId == this.state.ResolvedStatusLookupId &&
                                            <span>{cacheStore.GetString("ResolutionLabel")}</span>
                                        }
                                        {this.state.SelectedMedicalProblemStatusLookupId == this.state.InactiveStatusLookupId &&
                                            <span>{cacheStore.GetString("EndDateDLabel")}</span>

                                        }
                                    </td>
                                    <td className="c2">
                                        <div className="flex-container" style={{ justifyContent: "flex-start" }}>
                                            <TextboxControlWithDatePicker
                                                id="resolutionDP"
                                                textValue={this.state.ResolutionDateText}
                                                dateValue={this.state.ResolutionDate}
                                                onChange={(datePrecision: DateTimeWithPrecision) => {
                                                    this.ResolutionDateChanged(datePrecision, datePrecision.precision);
                                                }}
                                            />

                                            <div style={{ paddingLeft: "10px", marginTop: "5px" }} onClick={this.ResolutionDateApproxClicked.bind(this)}>
                                                <input disabled={resDateApproxdisabled === "disabled"} type="checkbox" onChange={this.ResolutionDateApproxClicked.bind(this)} checked={this.state.IsResolutionDateApproximative} />
                                                <span>Approx.</span>
                                            </div>
                                            <div style={{ paddingLeft: "10px", marginTop: "5px", cursor: "pointer" }}>
                                                <a style={{ cursor: "pointer" }} onClick={this.ResolutionDateTodayClicked.bind(this)}>
                                                    <span>
                                                        {cacheStore.GetString("TodayLabel")}
                                                    </span>
                                                </a>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            )}

                            {this.state.SelectedMedicalProblemStatusLookupId == this.state.ResolvedStatusLookupId && (
                                <tr>
                                    <td className="c1">
                                        <span>{cacheStore.GetString("ProcedureLabel")}</span>
                                    </td>
                                    <td className="c1">
                                        <TextAreaResizable
                                            minHeightPx={100}
                                            id="procedure"
                                            text={this.state.Procedure}
                                            isDisabled={false}
                                            onChange={value => this.ProcedureChanged(value)}
                                            setCursorStartOnFocus={false}
                                            resetTextOnIdChange={true}
                                        />
                                    </td>
                                </tr>
                            )}

                            {this.state.SelectedMedicalProblemStatusLookupId == this.state.ResolvedStatusLookupId && (
                                <tr>
                                    <td className="c1">
                                        <span>{cacheStore.GetString("ProcedureDateLabel")}</span>
                                    </td>
                                    <td className="c1">
                                        <div className="flex-container" style={{ justifyContent: "flex-start" }}>
                                            <TextboxControlWithDatePicker
                                                id="procedureDP"
                                                textValue={this.state.ProcedureDateText}
                                                dateValue={this.state.ProcedureDate}
                                                onChange={(datePrecision: DateTimeWithPrecision) => {
                                                    this.ProcedureDateChanged(datePrecision, datePrecision.precision);
                                                }}
                                            />

                                            <div style={{ paddingLeft: "10px", marginTop: "5px" }} onClick={this.ProcedureDateApproxClicked.bind(this)}>
                                                <input disabled={procedureDateApproxDisabled === "disabled"} type="checkbox" onChange={this.ProcedureDateApproxClicked.bind(this)} checked={this.state.IsProcedureDateApproximative} />
                                                <span>Approx.</span>
                                            </div>
                                            <div style={{ paddingLeft: "10px", marginTop: "5px", cursor: "pointer" }}>
                                                <a style={{ cursor: "pointer" }} onClick={this.ProcedureDateTodayClicked.bind(this)}>
                                                    <span>
                                                        {cacheStore.GetString("TodayLabel")}
                                                    </span>
                                                </a>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            )}

                            <tr>
                                <td className="c1" />
                                <td className="c1">
                                    <RadioButton
                                        radioInputGroupId="problemType"
                                        onePerLine
                                        items={Immutable.List(MedicalItemsMedicalProblemViewState.GetProblemTypeRadioButtonModels())}
                                        selectedItem={this.state.SelectedMedicalProblemSubType ?? MedicalItemsMedicalProblemViewState.GetProblemTypeRadioButtonModels()[0].Value}
                                        onChange={item => this.MedicalProblemTypeChange(item.Value)}
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            );
        };

        return (
            <BaseMedicalItemsView
                PatientContactId={this.props.PatientContactId}
                Model={this.props.Model}
                DetailsTitle={cacheStore.GetString("CppLabel") + " : " + cacheStore.GetString("ProblemsAndDiagnosesLabel")}
                MedicalItemRenderFunction={itemDetailsRenderFunction}
                MedicalItemEditRenderFunction={itemDetailsEditRenderFunction}
                ListItemRenderFunction={listItemRenderFunction}
                SaveFunction={this.Save.bind(this, false)}
                SavePlusFunction={this.SavePlus.bind(this)}
                emrType={MedicalItemTypeEnum.PastAndOngoingProblemsAndConditions}
                CheckIfSaveEnabledFunction={this.CheckIfSaveIsEnabled.bind(this)}
                toSearchableStringFunction={(items: MedicalItem[]) => this.searchMedicalItem(items)}
                showFavorites={this.props.showFavorites}
            />
        );
    }

    private SetEditedItemState(medItem: MedicalItem): void {
        const problem = medItem as MedicalItemProblemAndCondition;

        if (!problem || problem.IsCollapsed || problem.IsCommitted)
            return;

        const isSameItem = problem.displayId == this.state.DisplayId
            && problem.MedicalItemId == this.state.Id;

        if (!isSameItem) {
            const s = new MedicalItemsMedicalProblemViewState();

            s.Id = problem.MedicalItemId;
            s.DisplayId = problem.displayId;
            s.ProblemDescriptionContext = problem.displayId ?? problem.MedicalItemId;
            s.SelectedMedicalProblemStatusLookupId = problem.ProblemStatusLookupId ? problem.ProblemStatusLookupId : "";
            s.Description = problem.ProblemDescription == null ? "" : problem.ProblemDescription;
            s.LifeStageMode = problem.LifeStageMode;
            s.LifeStageStartDate = problem.LifeStageDate;
            s.LifeStageDateTextboxValue = s.LifeStageStartDate != null ? PatientMedicalItemsHelper.GetLifeStageDateString(s.LifeStageStartDate, problem.LifeStagePrecisionDate) : "";

            s.LifeStageAgeAtOnset = problem.LifeStageAge;
            s.LifeStageAgeSelectedgeAtOnsetUnit = problem.LifeStageAgeStatusLookupId;
            s.LifeStageSelectedLifestageLookupId = problem.LifeStageStatusLookupId;
            s.VisibleInCPP = problem.EMRTemplateIsVisibleInCPP;

            s.SelectedMedicalProblemSubType =
                problem.EMRTemplateMedicalItemSubType?.toString()
                ?? patientStore.SelectedMedicalProblemSubType?.toString()
                ?? MedicalItemSubtypeEnum.ProblemAndCondition_Ongoing.toString();

            s.LifeStageIsStartDateApproximative = problem.LifeStagePrecisionDate != LifeStageDatePrecisionEnum.Day;
            s.LifeStageDatePrecision = problem.LifeStagePrecisionDate;
            s.Procedure = problem.Procedure;
            s.ProcedureDate = problem.ProcedureDate;
            s.ProcedureDatePrecision = problem.PrecisionDateProcedure;
            s.ProcedureDateText = PatientMedicalItemsHelper.GetLifeStageDateString(s.ProcedureDate, s.ProcedureDatePrecision);
            s.IsProcedureDateApproximative = problem.PrecisionDateProcedure != LifeStageDatePrecisionEnum.Day;
            s.ResolutionDate = problem.DateResolved;
            s.ResolutionDatePrecision = problem.PrecisionDateResolved;
            s.ResolutionDateText = PatientMedicalItemsHelper.GetLifeStageDateString(s.ResolutionDate, s.ResolutionDatePrecision);
            s.IsResolutionDateApproximative = problem.PrecisionDateResolved != LifeStageDatePrecisionEnum.Day;

            s.QualifiedDetails = problem.QualifiedDetails;

            this.setState(s);
        }
        else if (problem.EMRTemplateIsVisibleInCPP != this.state.VisibleInCPP) {
            let s = new MedicalItemsMedicalProblemViewState();

            s.VisibleInCPP = problem.EMRTemplateIsVisibleInCPP;

            this.setState(s);
        }
    }

    private LifeStageModeChanged(newMode: LifeStageModeEnum): void {
        let s = new MedicalItemsMedicalProblemViewState();
        s.LifeStageMode = newMode;

        if (s.LifeStageMode == LifeStageModeEnum.Age) {
            // s.LifeStageDatePrecision = LifeStageDatePrecisionEnum.MonthYear;
            s.LifeStageAgeSelectedgeAtOnsetUnit = cacheStore.FindLookupByLookupCode("year", "DayYear").LookupID;
            s.LifeStageAgeAtOnset = undefined;
            s.LifeStageIsStartDateApproximative = false;
        }
        else if (s.LifeStageMode == LifeStageModeEnum.Date) {
            s.LifeStageIsStartDateApproximative = s.LifeStageDatePrecision != LifeStageDatePrecisionEnum.Day;
        }

        this.setState(s);
    }

    private LifeStageDateApproxChange(): void {
        if (this.state && this.state.LifeStageDatePrecision == LifeStageDatePrecisionEnum.MonthYear || this.state.LifeStageDatePrecision == LifeStageDatePrecisionEnum.Year)
            return;

        let s = new MedicalItemsMedicalProblemViewState();
        s.LifeStageIsStartDateApproximative = !this.state.LifeStageIsStartDateApproximative;
        s.LifeStageDatePrecision = s.LifeStageIsStartDateApproximative ? LifeStageDatePrecisionEnum.DayApprox : LifeStageDatePrecisionEnum.Day;

        this.setState(s);
    }

    private LifeStageDateChanged(date: DateTimeWithPrecision, precision: LifeStageDatePrecisionEnum = LifeStageDatePrecisionEnum.Day): void {
        const s = new MedicalItemsMedicalProblemViewState();

        if (date.value) {
            s.LifeStageStartDate = moment(date.value).toDate();

            if (precision == LifeStageDatePrecisionEnum.DayApprox || precision == LifeStageDatePrecisionEnum.Day) {
                s.LifeStageDatePrecision = (this.state.LifeStageIsStartDateApproximative == true) ?
                    LifeStageDatePrecisionEnum.DayApprox :
                    LifeStageDatePrecisionEnum.Day;

                s.LifeStageDateTextboxValue = moment(date.value).format("YYYY-MM-DD"); // always iso 900somthing format
            }
            else {
                s.LifeStageDatePrecision = precision;
                s.LifeStageDateTextboxValue = PatientMedicalItemsHelper.GetLifeStageDateString(date.value, precision);
                s.LifeStageIsStartDateApproximative = true;
            }

            s.LifeStageMode = LifeStageModeEnum.Date;
        }
        else if (IsNullOrWhiteSpace(date.text)) {
            s.LifeStageStartDate = null;
            s.LifeStageDateTextboxValue = "";
            s.LifeStageIsStartDateApproximative = false;
        }

        this.setState(s);
    }

    private LifeStageDateTodayClicked(): void {
        let s = new MedicalItemsMedicalProblemViewState();
        s.LifeStageDateTextboxValue = moment(Date.now()).format("YYYY-MM-DD");
        s.LifeStageStartDate = moment(Date.now()).toDate();
        s.LifeStageMode = LifeStageModeEnum.Date;

        s.LifeStageDatePrecision = (this.state.LifeStageIsStartDateApproximative == true) ?
            LifeStageDatePrecisionEnum.DayApprox :
            LifeStageDatePrecisionEnum.Day;

        this.setState(s);
    }

    private LifeStageChanged(lookupId: string): void {
        let s = new MedicalItemsMedicalProblemViewState();
        s.LifeStageSelectedLifestageLookupId = lookupId;
        s.LifeStageMode = LifeStageModeEnum.LifeStage;
        this.setState(s);
    }

    private LifeStageAgeChange(value: string): void {
        let s = new MedicalItemsMedicalProblemViewState();
        s.LifeStageAgeAtOnset = PatientMedicalItemsHelper.parseLifeStageAgeAtOnsetValue(value);
        s.LifeStageMode = LifeStageModeEnum.Age;

        this.setState(s);
    }

    private LifeStageAgeUnitChange(lookupId: string): void {
        let s = new MedicalItemsMedicalProblemViewState();
        s.LifeStageAgeSelectedgeAtOnsetUnit = lookupId;
        s.LifeStageMode = LifeStageModeEnum.Age;
        this.setState(s);
    }

    private onDiagnosticTextChange(str: string, qd: QualifiedDetail): void {
        if (qd != null) {
            let diagnostics = this.state.QualifiedDetails;
            let itemId = (qd.Id != null && qd.Id != GuidHelper.Empty()) ? qd.Id : qd.tmpIdForFrontEndOnly;
            let foundDiag: QualifiedDetail = ArrayHelper.Find(diagnostics, x => x.Id == itemId || x.tmpIdForFrontEndOnly == itemId);
            if (foundDiag != null) {
                let idx = diagnostics.indexOf(foundDiag);
                foundDiag.Text = str;
                diagnostics[idx] = foundDiag;

                this.mutateState(s => s.QualifiedDetails = diagnostics);
            }
        }
    }

    private onDiagnosticItemSelect(item: Diagnosis, qd: QualifiedDetail): void {
        const siteModel = cacheStore.GetSiteById(this.props.Model.get("Encounter")?.SiteId) ?? sessionStore.getCurrentEmployee().getActiveSite();
        const displayLocal = item.getLocaleFromSite(siteModel);

        if (item) {
            let diagnostics = this.state.QualifiedDetails;
            let itemId = (qd.Id != null && qd.Id != GuidHelper.Empty()) ? qd.Id : qd.tmpIdForFrontEndOnly;
            let foundDiag: QualifiedDetail = ArrayHelper.Find(diagnostics, x => x.Id == itemId || x.tmpIdForFrontEndOnly == itemId);
            if (foundDiag) {
                let idx = diagnostics.indexOf(foundDiag);
                foundDiag.Text = item.getName(displayLocal);
                foundDiag.Code = item.Code;
                foundDiag.isEditMode = false;
                foundDiag.SourceDictionary = item.Type;
                diagnostics[idx] = foundDiag;

                this.mutateState(s => s.QualifiedDetails = diagnostics);
            }
        }
    }

    private onFavoriteDiagnosticItemSelect(item: DiagnosisModel, qualifiedDetail: QualifiedDetail): void {
        if (item) {
            let qualifiedDetailsCount = this.state.QualifiedDetails.length + 1;
            const qualifiedDetails = item.Model.DiagnosisItems.map(item => {
                const detail = new QualifiedDetail();
                detail.Id = item.Id;
                detail.Code = item.Code;
                detail.Text = item.Text;
                detail.StatusLookupId = item.StatusLookupId;
                detail.SourceDictionary = item.SourceDictionary;
                detail.Position = qualifiedDetailsCount;
                detail.ProblemItemId = item.DiagnosisId;
                qualifiedDetailsCount++;
                return detail;
            });
            let existingQualifiedDetails = this.state.QualifiedDetails.filter(detail => {
                if (detail.CodeId != qualifiedDetail.CodeId)
                    return detail;
            });
            existingQualifiedDetails = existingQualifiedDetails.concat(qualifiedDetails);
            const newDescription = this.state.Description + "\n" + item.Model.ProblemDescription;

            this.mutateState(s => {
                s.ProblemDescriptionContext = GuidHelper.NewGuid();
                s.Description = newDescription.trimStart();
                s.QualifiedDetails = existingQualifiedDetails;
            });
        }
    }

    private onDiagnosticStatusSelect(statusId: string, qd: QualifiedDetail): void {
        if (!(qd == null)) {
            const diagnostics = this.state.QualifiedDetails;
            const itemId = (qd.Id != null && qd.Id != GuidHelper.Empty()) ? qd.Id : qd.tmpIdForFrontEndOnly;
            const foundDiag: QualifiedDetail = ArrayHelper.Find(diagnostics, (x => x.Position == qd.Position && (x.Id == itemId || x.tmpIdForFrontEndOnly == itemId)));
            if (foundDiag) {
                const idx = diagnostics.indexOf(foundDiag);
                foundDiag.StatusLookupId = statusId;
                diagnostics[idx] = foundDiag;

                this.mutateState(s => s.QualifiedDetails = diagnostics);
            }
        }
    }

    private onDiagnosticRemoveClicked(qd: QualifiedDetail): void {
        this.mutateState(s => {
            let diagnostics: QualifiedDetail[] = [];

            if (!!qd && this.state.QualifiedDetails.length > 1) {
                if (qd.Id != null && qd.Id != GuidHelper.Empty())
                    diagnostics = this.state.QualifiedDetails.filter(x => x.Id != qd.Id || x.Position != qd.Position);
                else if (qd.tmpIdForFrontEndOnly != null)
                    diagnostics = this.state.QualifiedDetails.filter(x => x.tmpIdForFrontEndOnly != qd.tmpIdForFrontEndOnly);

                s.QualifiedDetails = diagnostics;
            }
            else if (!s.QualifiedDetails || s.QualifiedDetails.length <= 1) {
                s.QualifiedDetails = [
                    new QualifiedDetail({
                        Id: GuidHelper.NewGuid(),
                        Code: "",
                        Text: "",
                        StatusLookupId: "",
                        Position: 0
                    })
                ];
            }

            s.QualifiedDetails = s.QualifiedDetails.map(p => new QualifiedDetail({ ...p, Position: s.QualifiedDetails.indexOf(p) }));
        });
    }

    private DiagnosticAddClicked(): void {
        let s = new MedicalItemsMedicalProblemViewState();
        s.QualifiedDetails = this.state.QualifiedDetails;
        s.QualifiedDetails.push(this.DiagnosticNewClicked(s.QualifiedDetails));
        this.setState(s);
    }

    private DiagnosticEditClicked(qd: QualifiedDetail): void {
        const s = new MedicalItemsMedicalProblemViewState();

        if (!(qd == null)) {
            const diagnostics = this.state.QualifiedDetails;
            const itemId = (qd.Id != null && qd.Id != GuidHelper.Empty()) ? qd.Id : qd.tmpIdForFrontEndOnly;
            const foundDiag: QualifiedDetail = ArrayHelper.Find(diagnostics, x => x.Position == qd.Position && (x.Id == itemId || x.tmpIdForFrontEndOnly == itemId));
            if (foundDiag != null) {
                const idx = diagnostics.indexOf(foundDiag);
                foundDiag.isEditMode = !foundDiag.isEditMode;
                diagnostics[idx] = foundDiag;
                s.QualifiedDetails = diagnostics;
                this.setState(s);
            }
        }
    }

    private DiagnosticNewClicked(qualifiedDetails: QualifiedDetail[] = null): QualifiedDetail {
        var emptyDiagnostic = new QualifiedDetail();
        emptyDiagnostic.Id = GuidHelper.Empty();
        emptyDiagnostic.tmpIdForFrontEndOnly = sessionStore.newId();
        emptyDiagnostic.CodeId = GuidHelper.NewGuid();
        emptyDiagnostic.Code = "";
        emptyDiagnostic.Text = "";
        emptyDiagnostic.StatusLookupValue = "";
        emptyDiagnostic.StatusLookupId = "";
        emptyDiagnostic.isEditMode = true;
        emptyDiagnostic.Position = (qualifiedDetails != null && qualifiedDetails.length > 0) ? qualifiedDetails.length : 0;

        return emptyDiagnostic;
    }

    private MedicalProblemDescriptionChanged(value: string): void {
        let s = new MedicalItemsMedicalProblemViewState();

        s.Description = value;

        this.setState(s);
    }

    private MedicalProblemStatusSelected(statusId: string): void {
        let s = new MedicalItemsMedicalProblemViewState();
        s.SelectedMedicalProblemStatusLookupId = statusId;
        this.setState(s);
    }

    private MedicalProblemTypeChange(selectedSubType: string): void {
        if (selectedSubType) {
            let s = new MedicalItemsMedicalProblemViewState();
            s.SelectedMedicalProblemSubType = selectedSubType;
            this.setState(s);
        }
    }

    private ResolutionDateChanged(date: DateTimeWithPrecision, precision: LifeStageDatePrecisionEnum = LifeStageDatePrecisionEnum.Day): void {
        const s = new MedicalItemsMedicalProblemViewState();

        if (date.value) {
            s.ResolutionDate = moment(date.value).toDate();

            if (precision == LifeStageDatePrecisionEnum.DayApprox || precision == LifeStageDatePrecisionEnum.Day) {
                s.ResolutionDatePrecision = (this.state.IsResolutionDateApproximative == true) ?
                    LifeStageDatePrecisionEnum.DayApprox :
                    LifeStageDatePrecisionEnum.Day;

                s.ResolutionDateText = moment(date.value).format("YYYY-MM-DD"); // always iso 900somthing format
            }
            else {
                s.ResolutionDatePrecision = precision;
                s.ResolutionDateText = PatientMedicalItemsHelper.GetLifeStageDateString(date.value, precision);
                s.IsResolutionDateApproximative = true;
            }
        }

        if (IsNullOrWhiteSpace(date.text)) {
            s.ResolutionDate = null;
            s.ResolutionDateText = "";
            s.IsResolutionDateApproximative = false;
        }

        this.setState(s);
    }

    private ResolutionDateApproxClicked(): void {
        if (this.state && this.state.ResolutionDatePrecision == LifeStageDatePrecisionEnum.MonthYear
            || this.state.ResolutionDatePrecision == LifeStageDatePrecisionEnum.Year)
            return;

        let s = new MedicalItemsMedicalProblemViewState();
        s.IsResolutionDateApproximative = !this.state.IsResolutionDateApproximative;
        s.ResolutionDatePrecision = s.IsResolutionDateApproximative ? LifeStageDatePrecisionEnum.DayApprox : LifeStageDatePrecisionEnum.Day;
        this.setState(s);
    }

    private ResolutionDateTodayClicked(): void {
        let s = new MedicalItemsMedicalProblemViewState();
        s.ResolutionDateText = moment(Date.now()).format("YYYY-MM-DD");
        s.ResolutionDate = moment(Date.now()).toDate();

        s.ResolutionDatePrecision = (this.state.IsResolutionDateApproximative == true) ?
            LifeStageDatePrecisionEnum.DayApprox :
            LifeStageDatePrecisionEnum.Day;

        this.setState(s);
    }

    private ProcedureDateChanged(date: DateTimeWithPrecision, precision: LifeStageDatePrecisionEnum = LifeStageDatePrecisionEnum.Day): void {
        const s = new MedicalItemsMedicalProblemViewState();

        if (date.value) {
            s.ProcedureDate = moment(date.value).toDate();

            if (precision == LifeStageDatePrecisionEnum.DayApprox || precision == LifeStageDatePrecisionEnum.Day) {
                s.ProcedureDatePrecision = (this.state.IsProcedureDateApproximative == true) ?
                    LifeStageDatePrecisionEnum.DayApprox :
                    LifeStageDatePrecisionEnum.Day;

                s.ProcedureDateText = moment(date.value).format("YYYY-MM-DD"); // always iso 900somthing format
            }
            else {
                s.ProcedureDatePrecision = precision;
                s.ProcedureDateText = PatientMedicalItemsHelper.GetLifeStageDateString(date.value, precision);
                s.IsProcedureDateApproximative = true;
            }
        }
        else if (IsNullOrWhiteSpace(date.text)) {
            s.ProcedureDate = null;
            s.ProcedureDateText = "";
            s.IsProcedureDateApproximative = false;
        }
        this.setState(s);
    }

    private ProcedureDateApproxClicked(): void {
        if (this.state && this.state.ProcedureDatePrecision == LifeStageDatePrecisionEnum.MonthYear
            || this.state.ProcedureDatePrecision == LifeStageDatePrecisionEnum.Year)
            return;

        let s = new MedicalItemsMedicalProblemViewState();
        s.IsProcedureDateApproximative = !this.state.IsProcedureDateApproximative;
        s.ProcedureDatePrecision = s.IsProcedureDateApproximative ? LifeStageDatePrecisionEnum.DayApprox : LifeStageDatePrecisionEnum.Day;
        this.setState(s);
    }

    private ProcedureDateTodayClicked(): void {
        let s = new MedicalItemsMedicalProblemViewState();
        s.ProcedureDateText = moment(Date.now()).format("YYYY-MM-DD");
        s.ProcedureDate = moment(Date.now()).toDate();

        s.ProcedureDatePrecision = (this.state.IsProcedureDateApproximative == true) ?
            LifeStageDatePrecisionEnum.DayApprox :
            LifeStageDatePrecisionEnum.Day;

        this.setState(s);
    }

    private ProcedureChanged(value: string): void {
        let s = new MedicalItemsMedicalProblemViewState();
        s.Procedure = value;
        this.setState(s);
    }

    private async Save(isSavePlus: boolean = false): Promise<void> {
        const medicalItems = this.props.Model.get("MedicalItemsList").toArray();
        let editMedicalItem = medicalItems.firstOrDefault(x => x.IsCommitted == false) as MedicalItemProblemAndCondition;
        editMedicalItem = MyleHelper.CloneObject(editMedicalItem);

        const medicalItemsToSend: MedicalItemProblemAndCondition[] = [];

        if (editMedicalItem != null) {
            editMedicalItem.ProblemDescription = this.state.Description;
            editMedicalItem.DateResolved = cacheStore.Helper.CreateDateAsUTC(this.state.ResolutionDate);
            editMedicalItem.PrecisionDateResolved = this.state.ResolutionDatePrecision;
            editMedicalItem.ProcedureDate = cacheStore.Helper.CreateDateAsUTC(this.state.ProcedureDate);
            editMedicalItem.PrecisionDateProcedure = this.state.ProcedureDatePrecision;
            editMedicalItem.Procedure = this.state.Procedure;
            editMedicalItem.ProblemStatusLookupId = this.state.SelectedMedicalProblemStatusLookupId;
            editMedicalItem.LifeStageAge = this.state.LifeStageAgeAtOnset;
            editMedicalItem.LifeStageAgeStatusLookupId = this.state.LifeStageAgeSelectedgeAtOnsetUnit;
            editMedicalItem.LifeStageDate = cacheStore.Helper.CreateDateAsUTC(this.state.LifeStageStartDate);
            editMedicalItem.LifeStageMode = this.state.LifeStageMode;
            editMedicalItem.LifeStagePrecisionDate = this.state.LifeStageDatePrecision;
            editMedicalItem.LifeStageStatusLookupId = this.state.LifeStageSelectedLifestageLookupId;
            editMedicalItem.EMRTemplateIsVisibleInCPP = this.state.VisibleInCPP;
            editMedicalItem.EMRTemplateMedicalItemSubType = this.state.SelectedMedicalProblemSubType ? parseInt(this.state.SelectedMedicalProblemSubType) : null;

            // set the "anteriority consultation id" meaning  - linking the problem with the actual encounter it was originaly created on
            if (editMedicalItem.AnteriorityConsultationId == GuidHelper.Empty().toString() || editMedicalItem.AnteriorityConsultationId == null || editMedicalItem.AnteriorityConsultationId == undefined) {
                editMedicalItem.AnteriorityConsultationId = editMedicalItem.RelatedEncounterId;
            }

            // for other diagnostics/qualified details, clone first object and set corrresponding object
            if (this.state.QualifiedDetails != null && this.state.QualifiedDetails.length > 0) {
                editMedicalItem.QualifiedDetails = this.state.QualifiedDetails;
            }

            medicalItemsToSend.push(editMedicalItem);
        }

        if (patientStore.IsMedicalItemFromOutsideNote) {
            await PatientMedicalItemsHelper.setOutsideNoteEncounter(medicalItemsToSend);
        }

        var operation = new LocalEvent(LocalPatientOperations.MedicalItems.Save);
        operation.AddItem("view", MedicalItemsTab.MedicalProblem);
        operation.AddItem("editedItems", medicalItemsToSend);
        if (isSavePlus)
            operation.AddItem("isSavePlus", true);

        LocalEventDispatcher.dispatch(operation);
    }

    private async SavePlus(): Promise<void> {
        await this.Save(true);
    }

    private CheckIfSaveIsEnabled(): boolean {
        var isSaveEnabled = false;

        if (sessionStore.getCurrentEmployee().getActiveSite().Model.EnforceDiagnosisSelection == true) {
            isSaveEnabled = this.state.QualifiedDetails != null &&
                this.state.QualifiedDetails.length > 0 &&
                this.state.QualifiedDetails.every(x => x.Text != null && x.Text.trim().length > 0 && x.Code != null && x.Code.trim().length > 0 && !x.isEditMode);
        }
        else {
            isSaveEnabled = this.state.QualifiedDetails != null &&
                this.state.QualifiedDetails.length > 0 &&
                this.state.QualifiedDetails.some(x => x.Text != null && x.Text.trim().length > 0);
        }

        return isSaveEnabled;
    }

    private searchMedicalItem(items: MedicalItem[]): MedicalItemProblemAndCondition[] {
        let medicalitems: MedicalItemProblemAndCondition[] = items as MedicalItemProblemAndCondition[]; // cast MedicalItem to problems and conditions
        let resultFiltered: MedicalItemProblemAndCondition[] = [];
        let qualifiedDetails: QualifiedDetail[] = [];
        let problemItems: MedicalItemProblemAndCondition[] = [];

        // Search qualifiedDetail by name
        medicalitems.forEach(mi => {
            qualifiedDetails = qualifiedDetails.concat(mi.QualifiedDetails.filterSearch(this.props.Model.get("SearchTerm"), qd => {
                // items cannot be null for search, replace them with empty string
                let name = MyleHelper.replaceNullWithEmptyString(qd.Text);
                let status = MyleHelper.replaceNullWithEmptyString(qd.StatusLookupValue);

                return name + " " + status;
            }));
        });

        // Search problems by life stages
        problemItems = problemItems.concat(medicalitems.filterSearch(this.props.Model.get("SearchTerm"), mi_item => {
            // items cannot be null for search, replace them with empty string
            let lifeStageDate = MyleHelper.replaceNullWithEmptyString(mi_item.LifeStageDate);
            let lifeStageStatusLookup = (cacheStore.GetLookupById(mi_item.LifeStageStatusLookupId) != null) ? cacheStore.GetLookupById(mi_item.LifeStageStatusLookupId).GetValue() : "";
            let lifeStageAge = MyleHelper.replaceNullWithEmptyString(mi_item.LifeStageAge);

            return lifeStageDate + " " + lifeStageStatusLookup + " " + lifeStageAge;
        }));

        // get corresponding problem for every qualified details found in search
        // Add them to search result array
        medicalitems.map(mi => {
            qualifiedDetails.forEach(qd => {
                if (mi.MedicalItemId == qd.ProblemItemId && resultFiltered.indexOf(mi) === -1) {
                    resultFiltered.push(mi);
                }
            });
        });

        // Add the problems found by search only when they are not already in the search result
        medicalitems.map(mi => {
            problemItems.forEach(problem => {
                if (mi.MedicalItemId == problem.MedicalItemId && resultFiltered.indexOf(mi) === -1) {
                    resultFiltered.push(mi);
                }
            });
        });

        return resultFiltered;
    }

    private getDateApproxString(date: Date, precisionDate: number): string {
        let dateString = "";
        dateString = PatientMedicalItemsHelper.GetLifeStageDateString(date, precisionDate);
        if (!IsNullOrWhiteSpace(dateString) && precisionDate == LifeStageDatePrecisionEnum.DayApprox)
            dateString = "≈ " + dateString;

        return dateString;
    }
}

export { MedicalItemsMedicalProblemView };
